import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';

import { normalizeStatus } from '../../../config/layerDictionary.js';
import API_URL from '../../../config/api.js';
import { getUserCompany, isUserAdmin } from '../../../utils/auth.js';
import { layerNameToApiTable } from '../config/layerNameToApiTable.js';
import {
	completePendingReview,
	downloadPendingReviewReport,
	fetchPendingReviewDetail,
	fetchServerDateTime,
	reopenPendingReview,
	updateReviewStatusCache,
} from '../services/pendingReviewApi.js';
import { setPendingReviewPinStatus } from '../services/pendingReviewPin.js';

import './style/PendingReviewForm.css';

const firstValue = (properties, keys, fallback = null) => {
	for (const key of keys) {
		const value = properties?.[key];
		if (
			value !== null &&
			value !== undefined &&
			String(value).trim() !== ''
		) {
			return value;
		}
	}
	return fallback;
};

const formatResponsibleCompany = value => {
	const company = String(value ?? '').trim();
	if (!company) return 'Empresa não identificada';

	return /^esteio(?:[\s_-]*\d+)?$/i.test(company) ? 'Esteio' : company;
};

const normalizeLote = value => {
	if (value === null || value === undefined) return null;

	const match = String(value).match(/\d+/);
	if (!match) return null;

	const parsed = Number.parseInt(match[0], 10);
	return Number.isInteger(parsed) ? parsed : null;
};

const formatBeDisplay = value => {
	if (value === null || value === undefined) return '-';

	const text = String(value).trim().toUpperCase();
	if (!text) return '-';

	const match = text.match(/^(?:BE)?\s*0*(\d+)$/i);
	if (!match) return text;

	const parsed = Number.parseInt(match[1], 10);
	if (Number.isNaN(parsed)) return text;

	return String(parsed).padStart(2, '0');
};

const formatDateTime = value => {
	if (!value) return '-';

	const date = value instanceof Date ? value : new Date(value);
	if (Number.isNaN(date.getTime())) return '-';

	return date.toLocaleString('pt-BR', {
		day: '2-digit',
		month: '2-digit',
		year: 'numeric',
		hour: '2-digit',
		minute: '2-digit',
		second: '2-digit',
		hour12: false,
		timeZone: 'America/Sao_Paulo',
	});
};

const getLookupData = review => {
	const properties = review?.feature?.properties || {};
	const serviceKey = layerNameToApiTable[review?.layerName];
	const be = firstValue(properties, ['be', 'BE']);
	const bm = firstValue(properties, ['bm', 'BM']);
	const primaryBe = be !== null ? be : bm;
	const lote = normalizeLote(firstValue(properties, ['lote', 'LOTE']));
	const bloco = firstValue(properties, [
		'blocos',
		'bloco',
		'BLOCOS',
		'BLOCO',
	]);

	return {
		serviceKey,
		be:
			primaryBe !== null
				? String(primaryBe).trim().toUpperCase()
				: null,
		bm: bm !== null ? String(bm).trim().toUpperCase() : null,
		lote,
		bloco: bloco !== null ? String(bloco).trim().toUpperCase() : null,
	};
};

const getDetailedReports = (data, lookup) => {
	const reports =
		Array.isArray(data.relatoriosDetalhados) && data.relatoriosDetalhados.length
			? data.relatoriosDetalhados
			: [
					{
						campo: 'BE',
						be: data.be || lookup.be,
						documento: data.documentoDetalhado,
					},
				];

	return [...reports]
		.sort(
			(a, b) =>
				Number.parseInt(formatBeDisplay(a.be), 10) -
				Number.parseInt(formatBeDisplay(b.be), 10)
		)
		.map(report => ({
			key: report.be,
			label: `BE${formatBeDisplay(report.be)}`,
			document: report.documento || null,
			url: report.documento?.url
				? `${API_URL}/api/Documentos/publico/abrir/${encodeURIComponent(
						report.documento.url
					)}`
				: null,
		}));
};

const PendingReviewForm = ({ open, review, onClose }) => {
	const [reviewData, setReviewData] = useState(null);
	const [checkedItems, setCheckedItems] = useState({});
	const [loading, setLoading] = useState(false);
	const [loadError, setLoadError] = useState('');
	const [saving, setSaving] = useState(false);
	const [saveError, setSaveError] = useState('');
	const [saveSuccess, setSaveSuccess] = useState('');
	const [visualDateTime, setVisualDateTime] = useState(null);
	const [detailedReports, setDetailedReports] = useState([]);
	const [openedReports, setOpenedReports] = useState({});
	const [downloadingReport, setDownloadingReport] = useState(false);
	const [reportDownloadError, setReportDownloadError] = useState('');
	const [reopenDialogOpen, setReopenDialogOpen] = useState(false);
	const [reopening, setReopening] = useState(false);
	const [reopenError, setReopenError] = useState('');

	const lookup = useMemo(() => getLookupData(review), [review]);
	const userIsAdmin = isUserAdmin();

	useEffect(() => {
		if (!open) {
			setVisualDateTime(null);
			return undefined;
		}

		let cancelled = false;
		let intervalId = null;

		const loadServerClock = async () => {
			setVisualDateTime(null);

			try {
				const serverDateTime = await fetchServerDateTime();
				if (cancelled || !serverDateTime) return;

				const serverBase = new Date(serverDateTime).getTime();
				if (Number.isNaN(serverBase)) return;

				const localBase = performance.now();

				const updateClock = () => {
					const elapsed = performance.now() - localBase;
					setVisualDateTime(new Date(serverBase + elapsed));
				};

				updateClock();
				intervalId = window.setInterval(updateClock, 1000);
			} catch (error) {
				console.error('Erro ao obter horário do servidor:', error);
			}
		};

		loadServerClock();

		return () => {
			cancelled = true;

			if (intervalId !== null) {
				window.clearInterval(intervalId);
			}
		};
	}, [open]);

	useEffect(() => {
		if (!open || !review) return undefined;

		let cancelled = false;

		const loadReview = async () => {
			setReviewData(null);
			setCheckedItems({});
			setDetailedReports([]);
			setOpenedReports({});
			setDownloadingReport(false);
			setReportDownloadError('');
			setReopenDialogOpen(false);
			setReopening(false);
			setReopenError('');
			setLoadError('');
			setSaveError('');
			setSaveSuccess('');

			if (
				!lookup.serviceKey ||
				!lookup.be ||
				!lookup.lote ||
				!lookup.bloco
			) {
				setLoadError(
					'Não foi possível identificar SERVIÇO, BE ou BM, LOTE e BLOCO desta geometria.'
				);
				return;
			}

			setLoading(true);

			try {
				const data = await fetchPendingReviewDetail(lookup);
				if (cancelled) return;

				setReviewData(data);
				setDetailedReports(getDetailedReports(data, lookup));
				setCheckedItems(
					Object.fromEntries(
						(data.itens || []).map(item => [
							item.id,
							Boolean(item.confirmado),
						])
					)
				);
			} catch (error) {
				if (cancelled) return;

				if (
					axios.isAxiosError(error) &&
					error.response?.status === 404
				) {
					setLoadError(
						error.response?.data?.message ||
							'Nenhuma pendência importada foi encontrada para este produto, BE, lote e bloco.'
					);
				} else if (
					axios.isAxiosError(error) &&
					error.response?.status === 403
				) {
					setLoadError(
						'Seu usuário não possui permissão para visualizar esta revisão.'
					);
				} else {
					console.error(
						'Erro ao carregar revisão do produto:',
						error
					);
					setLoadError(
						'Não foi possível carregar os dados da revisão.'
					);
				}
			} finally {
				if (!cancelled) setLoading(false);
			}
		};

		loadReview();

		return () => {
			cancelled = true;
		};
	}, [open, review, lookup]);

	if (!open || !review) return null;

	const properties = review.feature?.properties || {};
	const rawStatus = firstValue(
		properties,
		['status', 'STATUS'],
		'Aprovado com Pendência'
	);
	const isCompleted =
		reviewData?.statusRevisao?.toUpperCase() === 'CONCLUIDA';
	const reviewItems = [...(reviewData?.itens || [])].sort(
		(a, b) => (a.ordem ?? 0) - (b.ordem ?? 0)
	);

	const formData = {
		be: formatBeDisplay(reviewData?.be || lookup.be),
		lote: reviewData?.lote ?? lookup.lote ?? '-',
		bloco: reviewData?.bloco || lookup.bloco || '-',
		produto: reviewData?.produtoNome || review.layerName || 'Produto',
		status: normalizeStatus(rawStatus),
		responsavel: formatResponsibleCompany(
			isCompleted
				? reviewData?.concluidoPorCompany
				: getUserCompany()
		),
	};

	// Os relatórios pertencem ao BE/BM do layer. Preservá-los separadamente
	// evita perder o relatório BM nas respostas de concluir ou reabrir.
	const allDetailedDocumentsAvailable =
		detailedReports.length > 0 && detailedReports.every(report => report.url);
	const allDetailedReportsOpened =
		detailedReports.length > 0 &&
		detailedReports.every(report => isCompleted || openedReports[report.key]);

	const allChecked =
		reviewItems.length > 0 &&
		reviewItems.every(item => Boolean(checkedItems[item.id]));

	const canSave =
		allChecked && allDetailedDocumentsAvailable && allDetailedReportsOpened;

	const handleDetailedReportOpen = key => {
		setOpenedReports(prev => ({ ...prev, [key]: true }));
		setSaveError('');
	};

	const getReceiptSuggestedName = () => {
		const safePart = value =>
			String(value ?? '')
				.trim()
				.replace(/[^a-zA-Z0-9_-]+/g, '_')
				.replace(/^_+|_+$/g, '');

		return (
			[
				'Comprovante_Revisao',
				`BE${safePart(formData.be)}`,
				`Lote${safePart(formData.lote)}`,
				`Bloco${safePart(formData.bloco)}`,
			]
				.filter(Boolean)
				.join('_') + '.pdf'
		);
	};

	const handleDownloadReceipt = async () => {
		if (!reviewData?.id || !isCompleted || downloadingReport) return;

		setDownloadingReport(true);
		setReportDownloadError('');

		try {
			await downloadPendingReviewReport(
				reviewData.id,
				getReceiptSuggestedName()
			);
		} catch (error) {
			console.error('Erro ao baixar comprovante:', error);
			setReportDownloadError(
				'Não foi possível baixar o comprovante da revisão.'
			);
		} finally {
			setDownloadingReport(false);
		}
	};

	const handleOpenReopenDialog = () => {
		if (!userIsAdmin || !isCompleted || reopening) return;

		setReopenError('');
		setSaveSuccess('');
		setReopenDialogOpen(true);
	};

	const handleCancelReopen = () => {
		if (reopening) return;

		setReopenDialogOpen(false);
		setReopenError('');
	};

	const handleReopen = async () => {
		if (!userIsAdmin || !reviewData?.id || !isCompleted || reopening) {
			return;
		}

		setReopening(true);
		setReopenError('');
		setSaveError('');
		setSaveSuccess('');
		setReportDownloadError('');

		try {
			const updatedReview = await reopenPendingReview(reviewData.id);

			setReviewData(updatedReview);
			updateReviewStatusCache(updatedReview);
			setCheckedItems(
				Object.fromEntries(
					(updatedReview.itens || []).map(item => [
						item.id,
						Boolean(item.confirmado),
					])
				)
			);

			// Ao reabrir, a leitura obrigatória de todos os relatórios é reiniciada.
			setOpenedReports({});

			if (review.feature?.properties) {
				review.feature.properties._revisao_id = updatedReview.id;
				review.feature.properties._revisao_status =
					updatedReview.statusRevisao;
				review.feature.properties._revisao_versao =
					updatedReview.versao;
			}

			setPendingReviewPinStatus(review.marker, updatedReview.statusRevisao);
			setReopenDialogOpen(false);
			setSaveSuccess(
				'Revisão reaberta com sucesso. O PIN voltou para vermelho e o formulário pode ser preenchido novamente.'
			);
		} catch (error) {
			if (axios.isAxiosError(error)) {
				setReopenError(
					error.response?.status === 403
						? 'Somente administradores podem reabrir uma revisão.'
						: error.response?.data?.message ||
							'Não foi possível reabrir a revisão.'
				);
			} else {
				console.error('Erro ao reabrir revisão:', error);
				setReopenError('Não foi possível reabrir a revisão.');
			}
		} finally {
			setReopening(false);
		}
	};

	const handleToggle = id => {
		if (isCompleted || saving) return;

		setSaveError('');
		setCheckedItems(prev => ({
			...prev,
			[id]: !prev[id],
		}));
	};

	const handleSave = async () => {
		if (!reviewData || !canSave || isCompleted || saving) return;

		setSaving(true);
		setSaveError('');
		setSaveSuccess('');
		setReportDownloadError('');

		try {
			const updatedReview = await completePendingReview({
				reviewId: reviewData.id,
				version: reviewData.versao,
				itemIds: reviewItems.map(item => item.id),
			});

			setReviewData(updatedReview);
			updateReviewStatusCache(updatedReview);

			setCheckedItems(
				Object.fromEntries(
					(updatedReview.itens || []).map(item => [
						item.id,
						Boolean(item.confirmado),
					])
				)
			);

			if (review.feature?.properties) {
				review.feature.properties._revisao_id = updatedReview.id;
				review.feature.properties._revisao_status =
					updatedReview.statusRevisao;
				review.feature.properties._revisao_versao =
					updatedReview.versao;
			}

			setPendingReviewPinStatus(
				review.marker,
				updatedReview.statusRevisao
			);

			try {
				await downloadPendingReviewReport(
					updatedReview.id,
					getReceiptSuggestedName()
				);

				setSaveSuccess(
					'Revisão concluída e comprovante baixado com sucesso. O PIN agora está amarelo.'
				);
			} catch (downloadError) {
				console.error(
					'Revisão concluída, mas houve erro ao baixar o comprovante:',
					downloadError
				);

				setSaveSuccess(
					'Revisão concluída com sucesso. O PIN agora está amarelo.'
				);

				setReportDownloadError(
					'A revisão foi salva, mas o comprovante não pôde ser baixado. Use o botão BAIXAR COMPROVANTE.'
				);
			}
		} catch (error) {
			if (axios.isAxiosError(error)) {
				const message =
					error.response?.data?.message ||
					'Não foi possível concluir a revisão.';

				setSaveError(message);

				if (error.response?.status === 409) {
					try {
						const latest = await fetchPendingReviewDetail(lookup);

						setReviewData(latest);
						setDetailedReports(getDetailedReports(latest, lookup));
						setOpenedReports({});
						updateReviewStatusCache(latest);

						setCheckedItems(
							Object.fromEntries(
								(latest.itens || []).map(item => [
									item.id,
									Boolean(item.confirmado),
								])
							)
						);

						if (
							latest.statusRevisao?.toUpperCase() === 'CONCLUIDA'
						) {
							if (review.feature?.properties) {
								review.feature.properties._revisao_status =
									latest.statusRevisao;
							}

							setPendingReviewPinStatus(
								review.marker,
								latest.statusRevisao
							);
						}
					} catch (reloadError) {
						console.error(
							'Erro ao recarregar revisão após conflito:',
							reloadError
						);
					}
				}
			} else {
				console.error('Erro ao concluir revisão:', error);
				setSaveError('Não foi possível concluir a revisão.');
			}
		} finally {
			setSaving(false);
		}
	};

	return (
		<div className="pending-review-backdrop" role="presentation">
			<section
				className="pending-review-form"
				role="dialog"
				aria-modal="true"
				aria-labelledby="pending-review-title"
			>
				<button
					type="button"
					className="pending-review-close"
					onClick={onClose}
					aria-label="Fechar formulário de revisão"
				>
					×
				</button>

				<div className="pending-review-scroll">
					<header
						className="pending-review-title"
						id="pending-review-title"
					>
						FORMULÁRIO DE REVISÃO DE PRODUTO
					</header>

					{loading && (
						<div className="pending-review-loading" role="status">
							Carregando pendências do banco...
						</div>
					)}

					{!loading && loadError && (
						<div className="pending-review-error" role="alert">
							<strong>Revisão não disponível</strong>
							<span>{loadError}</span>
							<small>
								Chave procurada: {lookup.serviceKey || '-'} /{' '}
								{lookup.be || '-'} / Lote {lookup.lote || '-'} /
								Bloco {lookup.bloco || '-'}
							</small>
						</div>
					)}

					{!loading && !loadError && reviewData && (
						<>
							<div className="pending-review-data-table">
								<div className="pending-review-label">BE</div>
								<div className="pending-review-value">
									{formData.be}
								</div>

								<div className="pending-review-label">LOTE</div>
								<div className="pending-review-value">
									{formData.lote}
								</div>

								<div className="pending-review-label">
									BLOCO
								</div>
								<div className="pending-review-value">
									{formData.bloco}
								</div>

								<div className="pending-review-label">
									PRODUTO
								</div>
								<div className="pending-review-value">
									{formData.produto}
								</div>

								<div className="pending-review-label">
									STATUS
								</div>
								<div className="pending-review-value">
									{formData.status}
								</div>
							</div>

							<h3 className="pending-review-section-title">
								RESUMO DAS ANÁLISES
							</h3>

							<div className="pending-review-checklist">
								{reviewItems.length === 0 ? (
									<div className="pending-review-empty-items">
										Nenhum problema foi cadastrado para esta
										revisão.
									</div>
								) : (
									reviewItems.map(item => {
										const isChecked = Boolean(
											checkedItems[item.id]
										);

										return (
											<label
												className={`pending-review-check-row ${
													isChecked
														? 'is-checked'
														: 'is-unchecked'
												} ${isCompleted ? 'is-readonly' : ''}`}
												key={item.id}
											>
												<input
													type="checkbox"
													checked={isChecked}
													disabled={
														isCompleted || saving
													}
													onChange={() =>
														handleToggle(item.id)
													}
												/>
												<span>{item.descricao}</span>
											</label>
										);
									})
								)}
							</div>

							<h3 className="pending-review-section-title">
								{detailedReports.length > 1
									? 'RELATÓRIOS DETALHADOS'
									: 'RELATÓRIO DETALHADO'}
							</h3>
							<div className="pending-review-report-list">
								{detailedReports.map(report => {
									const reportOpened =
										isCompleted ||
										Boolean(openedReports[report.key]);

									return (
										<div
											key={report.key}
											className={`pending-review-report-placeholder ${
												report.url
													? reportOpened
														? 'is-opened'
														: 'is-pending'
													: 'is-missing'
											}`}
										>
											<strong>{report.label}</strong>
											{report.url ? (
												<>
													<div className="pending-review-report-status">
														<span aria-hidden="true">
															{reportOpened ? '✓' : '!'}
														</span>
														{reportOpened
															? 'Relatório aberto'
															: 'Leitura obrigatória antes de salvar'}
													</div>

													<a
														href={report.url}
														target="_blank"
														rel="noopener noreferrer"
														className="pending-review-detailed-report-link"
														onClick={() =>
															handleDetailedReportOpen(report.key)
														}
													>
														{reportOpened
															? 'Abrir relatório novamente'
															: 'Abrir relatório detalhado'}
														{' — '}
														{report.label}
													</a>

													<span className="pending-review-detailed-report-name">
														{report.document.nome}
													</span>
												</>
											) : (
												<>
													<strong>
														Relatório detalhado não localizado.
													</strong>
													<span>
														Não foi encontrado um relatório de fiscalização
														para {report.label} e este produto.
														O formulário não poderá ser salvo.
													</span>
												</>
											)}
										</div>
									);
								})}
							</div>

							<h3 className="pending-review-section-title">
								RESPONSÁVEL
							</h3>
							<div className="pending-review-responsible">
								{formData.responsavel}
							</div>

							<div className="pending-review-datetime">
								<strong>DATA/HORA</strong>
								<span>
									{isCompleted
										? formatDateTime(reviewData.concluidoEm)
										: visualDateTime
											? formatDateTime(visualDateTime)
											: 'Sincronizando horário...'}
								</span>
							</div>

							{isCompleted && (
								<div
									className="pending-review-readonly"
									role="status"
								>
									{userIsAdmin
										? 'Esta revisão está concluída. Como administrador, você pode reabri-la.'
										: 'Esta revisão já está concluída. Os dados estão disponíveis somente para leitura.'}
								</div>
							)}

							{saveSuccess && (
								<div
									className="pending-review-success"
									role="status"
								>
									{saveSuccess}
								</div>
							)}

							{saveError && (
								<div
									className="pending-review-save-error"
									role="alert"
								>
									{saveError}
								</div>
							)}

							{reportDownloadError && (
								<div
									className="pending-review-report-error"
									role="alert"
								>
									{reportDownloadError}
								</div>
							)}
						</>
					)}
				</div>

				{reopenDialogOpen && (
					<div className="pending-review-reopen-overlay">
						<div
							className="pending-review-reopen-dialog"
							role="alertdialog"
							aria-modal="true"
							aria-labelledby="pending-review-reopen-title"
						>
							<h3 id="pending-review-reopen-title">REABRIR REVISÃO</h3>
							<p>
								Tem certeza que deseja reabrir esta revisão?
								 O PIN voltará para vermelho, todos os itens serão
								 desmarcados e os relatórios detalhados deverão ser abertos
								 novamente antes de um novo salvamento.
							</p>
							{reopenError && (
								<div className="pending-review-reopen-error" role="alert">
									{reopenError}
								</div>
							)}
							<div className="pending-review-reopen-dialog-actions">
								<button
									type="button"
									className="pending-review-reopen-cancel"
									disabled={reopening}
									onClick={handleCancelReopen}
								>
									CANCELAR
								</button>
								<button
									type="button"
									className="pending-review-reopen-confirm"
									disabled={reopening}
									onClick={handleReopen}
								>
									{reopening ? 'REABRINDO...' : 'CONFIRMAR REABERTURA'}
								</button>
							</div>
						</div>
					</div>
				)}

				{!loading &&
					!loadError &&
					reviewData &&
					(isCompleted ? (
						<div className="pending-review-completed-actions">
							{userIsAdmin && (
								<button
									type="button"
									className="pending-review-reopen"
									disabled={reopening || downloadingReport}
									onClick={handleOpenReopenDialog}
									title="Reabrir revisão concluída. Disponível somente para administradores."
								>
									REABRIR REVISÃO
								</button>
							)}

							<button
								type="button"
								className="pending-review-download"
								disabled={downloadingReport || reopening}
								onClick={handleDownloadReceipt}
								title="Baixar comprovante da revisão."
							>
								{downloadingReport
									? 'BAIXANDO COMPROVANTE...'
									: 'BAIXAR COMPROVANTE'}
							</button>
						</div>
					) : (
						<button
							type="button"
							className="pending-review-save"
							disabled={!canSave || saving}
							onClick={handleSave}
							title={
								!allChecked
									? 'Marque todos os itens da revisão.'
									: !allDetailedDocumentsAvailable
										? 'Um dos relatórios detalhados não foi localizado.'
										: !allDetailedReportsOpened
											? 'Abra todos os relatórios detalhados antes de salvar.'
											: 'Salvar revisão e baixar comprovante.'
							}
						>
							{saving ? 'SALVANDO...' : 'SALVAR'}
						</button>
					))}
			</section>
		</div>
	);
};

export default PendingReviewForm;
